import { useMemo, useState } from "react";
import { useCustom, useCustomMutation, useGetLocale, useTranslate } from "@refinedev/core";
import { Button, Field, Modal, SearchIcon, SelectField, TextareaField, PlusCircleSolidIcon } from "@fawkes/ui";

import { localDateIso } from "../i18n/format";
import { WizardRunModal } from "./WizardsTile";
import "./LogboekTile.css";

/**
 * Logboek bij het dier — dagelijkse verzorgingsacties en observaties.
 *
 * <p>Een eigen registratie naast de Geschiedenis-tab, en met opzet: die tab toont wat er aan
 * *gegevens* wijzigde ("Dier bijgewerkt", met de velddiff erbij) en hoort onveranderlijk te blijven.
 * Dit toont wat er aan het dier *gedaan* is — uitgelaten, gevoerd, hok schoon — en dat is werk van
 * mensen die zich mogen vergissen. Vandaar dat een eigen regel te herstellen is.
 *
 * <p>De labels van de categorieën komen van de server (`categorieLabel`), niet uit een tabel hier:
 * anders gaan scherm en database uit de pas lopen zodra er één bijkomt. Wat het scherm wél zelf weet
 * is de kleur en het icoon — dat is opmaak, geen gegeven.
 *
 * <p>Regels die uit een afgevinkte taak volgden dragen `bewerkbaar: false`. Ze zijn zichtbaar maar
 * niet aan te passen; anders zou het logboek gaan afwijken van de taak waar het uit voortkwam.
 */

// ── wat de server stuurt (zie api LogboekDtos) ──────────────────────────────────────────────────
interface RegelResponse {
  id: string;
  categorie: string;
  categorieLabel: string;
  tekst: string;
  gebeurdOp: string;
  auteur: string | null;
  bron: string;
  taakId: string | null;
  bewerkbaar: boolean;
}

interface CategorieOptie {
  id: string;
  label: string;
}

interface LogboekResponse {
  dierId: number;
  regels: RegelResponse[];
  categorieen: CategorieOptie[];
}

/** Hoever terug de tegel standaard kijkt. Een week is één dienstrooster; verder is geschiedenis. */
const STANDAARD_DAGEN = 7;

/**
 * Wat elke dag terugkomt, in één tik.
 *
 * <p>Bewust kort en niet instelbaar: een lijst snelknoppen die per asiel uitdijt is geen snelkoppeling
 * meer. Wat hier niet in staat tik je met de hand, en dat hoort ook — de meeste logregels zijn geen
 * herhaling maar een waarneming.
 */
const SNELLE_ACTIES: { categorie: string; tekstKey: string }[] = [
  { categorie: "VERZORGING", tekstKey: "logboek.snel.uitgelaten" },
  { categorie: "VERZORGING", tekstKey: "logboek.snel.water" },
  { categorie: "SCHOONMAAK", tekstKey: "logboek.snel.hok" },
  { categorie: "VOEDING", tekstKey: "logboek.snel.goedGegeten" },
  { categorie: "VOEDING", tekstKey: "logboek.snel.slechtGegeten" },
];

export function LogboekTile({ animalId }: { animalId: string }) {
  const t = useTranslate();
  const locale = useGetLocale()() ?? "nl";

  const [dagen, setDagen] = useState<string>(String(STANDAARD_DAGEN));
  const [categorie, setCategorie] = useState("");
  const [zoek, setZoek] = useState("");
  const [allesOpen, setAllesOpen] = useState(false);
  const [nieuwOpen, setNieuwOpen] = useState(false);
  const [snelOpen, setSnelOpen] = useState(false);
  const [fout, setFout] = useState<string | null>(null);

  const query = new URLSearchParams();
  if (dagen) query.set("dagen", dagen);
  if (categorie) query.set("categorie", categorie);
  if (zoek.trim()) query.set("zoek", zoek.trim());
  const suffix = query.toString();

  const { data, isLoading, isError, refetch } = useCustom({
    url: `animals/${animalId}/logboek${suffix ? `?${suffix}` : ""}`,
    method: "get",
  });
  const payload = data?.data as LogboekResponse | undefined;
  const regels = payload?.regels ?? [];
  const categorieen = payload?.categorieen ?? [];

  const { mutate, isLoading: bezig } = useCustomMutation();

  // Verzorgingslogboeken: wizards die het asiel per diersoort als dagformulier gebruikt ("schoonmaakformulier
  // kat"). Ze staan hier als knop en niet tussen de wizards; een ingevulde ronde komt als regel in het logboek.
  const { data: wizardData } = useCustom({ url: `animals/${animalId}/wizards/logboek`, method: "get" });
  const logboekWizards: { definitionId: string; naam: string; categorie: string }[] =
    Array.isArray(wizardData?.data) ? (wizardData.data as never) : [];
  const [wizardOpen, setWizardOpen] = useState<string | null>(null);

  const schrijf = (categorieId: string, tekst: string, daarna?: () => void) => {
    setFout(null);
    mutate(
      {
        url: `animals/${animalId}/logboek`,
        method: "post",
        values: { categorie: categorieId, tekst },
        successNotification: false,
        errorNotification: false,
      },
      {
        onSuccess: () => {
          void refetch();
          daarna?.();
        },
        onError: () => setFout(t("logboek.opslaanMislukt")),
      },
    );
  };

  const groepen = useMemo(() => groepeerPerDag(regels, locale, t), [regels, locale, t]);

  return (
    <div className="logboek">
      <div className="logboek__filters">
        <SelectField
          label={t("logboek.periode")}
          value={dagen}
          onChange={(e) => setDagen(e.target.value)}
          options={[
            { value: "7", label: t("logboek.periode.week") },
            { value: "30", label: t("logboek.periode.maand") },
            { value: "90", label: t("logboek.periode.kwartaal") },
            { value: "", label: t("logboek.periode.alles") },
          ]}
        />
        <SelectField
          label={t("logboek.categorie")}
          value={categorie}
          onChange={(e) => setCategorie(e.target.value)}
          options={[
            { value: "", label: t("logboek.categorie.alle") },
            ...categorieen.map((c) => ({ value: c.id, label: c.label })),
          ]}
        />
        <Field
          label={t("logboek.zoeken")}
          icon={<SearchIcon width={14} height={14} />}
          type="search"
          autoComplete="off"
          value={zoek}
          onChange={(e) => setZoek(e.target.value)}
          placeholder={t("logboek.zoekenPlaceholder")}
        />
      </div>

      <p className="logboek__uitleg">{t("logboek.uitleg")}</p>

      {fout && <p className="fawkes-error" role="alert">{fout}</p>}

      <div className="logboek__lijst">
        {isLoading ? <p className="logboek__leeg">{t("common.loading")}</p> : null}
        {isError ? <p className="logboek__leeg">{t("logboek.ophalenMislukt")}</p> : null}
        {!isLoading && !isError && regels.length === 0 ? (
          <p className="logboek__leeg">{t("logboek.leeg")}</p>
        ) : null}

        {groepen.map((groep) => (
          <div key={groep.sleutel}>
            <h4 className="logboek__dag">{groep.kop}</h4>
            {groep.regels.map((regel) => (
              <LogRegel key={regel.id} regel={regel} locale={locale} t={t} />
            ))}
          </div>
        ))}
      </div>

      <div className="logboek__acties">
        <Button variant="secondary" onClick={() => setAllesOpen(true)}>
          {t("logboek.allesBekijken")}
        </Button>
        <Button variant="primary" onClick={() => setNieuwOpen(true)} disabled={bezig}>
          <PlusCircleSolidIcon width={18} height={18} /> {t("logboek.nieuw")}
        </Button>
        <Button variant="secondary" onClick={() => setSnelOpen(true)} disabled={bezig}>
          {t("logboek.snelleActies")}
        </Button>
        {logboekWizards.map((w) => (
          <Button key={w.definitionId} variant="secondary" onClick={() => setWizardOpen(w.definitionId)}>
            {w.naam}
          </Button>
        ))}
      </div>

      {wizardOpen && (
        <WizardRunModal
          animalId={animalId}
          definitionId={wizardOpen}
          onKlaar={() => { setWizardOpen(null); void refetch(); }}
          onClose={() => setWizardOpen(null)}
        />
      )}

      {nieuwOpen && (
        <NieuweRegel
          categorieen={categorieen}
          bezig={bezig}
          onClose={() => setNieuwOpen(false)}
          onSave={(categorieId, tekst) => schrijf(categorieId, tekst, () => setNieuwOpen(false))}
        />
      )}

      {snelOpen && (
        <Modal open title={t("logboek.snelleActies")} onClose={() => setSnelOpen(false)}>
          <p className="fawkes-muted">{t("logboek.snelUitleg")}</p>
          <div className="logboek__snel">
            {SNELLE_ACTIES.map((actie) => (
              <Button
                key={actie.tekstKey}
                variant="secondary"
                disabled={bezig}
                onClick={() => schrijf(actie.categorie, t(actie.tekstKey), () => setSnelOpen(false))}
              >
                {t(actie.tekstKey)}
              </Button>
            ))}
          </div>
        </Modal>
      )}

      {allesOpen && (
        <Modal open title={t("logboek.allesTitel")} onClose={() => setAllesOpen(false)}>
          <AlleRegels animalId={animalId} locale={locale} t={t} />
        </Modal>
      )}
    </div>
  );
}

function LogRegel({
  regel,
  locale,
  t,
}: {
  regel: RegelResponse;
  locale: string;
  t: (key: string, options?: unknown) => string;
}) {
  return (
    <div className="logboek__regel">
      <span className="logboek__tijd">{tijd(regel.gebeurdOp, locale)}</span>
      <span className="logboek__stip" aria-hidden="true" />
      <span className={`logboek__chip logboek__chip--${regel.categorie}`}>
        <CategorieIcoon categorie={regel.categorie} />
        {regel.categorieLabel}
      </span>
      <div className="logboek__tekst">
        <p className="logboek__wat">{regel.tekst}</p>
        <p className="logboek__door">
          {regel.auteur ? t("logboek.door", { args: [regel.auteur] }) : t("logboek.doorOnbekend")}
          {!regel.bewerkbaar && (
            <span className="logboek__bron"> · {t(regel.bron === "WIZARD" ? "logboek.uitFormulier" : "logboek.uitTaak")}</span>
          )}
        </p>
      </div>
    </div>
  );
}

/** Het hele logboek, zonder periodefilter — "Alles bekijken" uit het ontwerp. */
function AlleRegels({
  animalId,
  locale,
  t,
}: {
  animalId: string;
  locale: string;
  t: (key: string, options?: unknown) => string;
}) {
  const { data, isLoading } = useCustom({ url: `animals/${animalId}/logboek`, method: "get" });
  const regels = (data?.data as LogboekResponse | undefined)?.regels ?? [];
  const groepen = useMemo(() => groepeerPerDag(regels, locale, t), [regels, locale, t]);

  if (isLoading) return <p className="fawkes-muted">{t("common.loading")}</p>;
  if (regels.length === 0) return <p className="fawkes-muted">{t("logboek.leeg")}</p>;

  return (
    <div className="logboek">
      <div className="logboek__lijst" style={{ maxHeight: "60vh" }}>
        {groepen.map((groep) => (
          <div key={groep.sleutel}>
            <h4 className="logboek__dag">{groep.kop}</h4>
            {groep.regels.map((regel) => (
              <LogRegel key={regel.id} regel={regel} locale={locale} t={t} />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function NieuweRegel({
  categorieen,
  bezig,
  onClose,
  onSave,
}: {
  categorieen: CategorieOptie[];
  bezig: boolean;
  onClose: () => void;
  onSave: (categorie: string, tekst: string) => void;
}) {
  const t = useTranslate();
  const [categorie, setCategorie] = useState(categorieen[0]?.id ?? "VERZORGING");
  const [tekst, setTekst] = useState("");
  const klaar = tekst.trim().length > 0;

  return (
    <Modal
      open
      title={t("logboek.nieuw")}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t("common.cancel")}
          </Button>
          <Button variant="primary" disabled={!klaar || bezig} onClick={() => onSave(categorie, tekst.trim())}>
            {t("common.save")}
          </Button>
        </>
      }
    >
      <SelectField
        label={t("logboek.categorie")}
        value={categorie}
        onChange={(e) => setCategorie(e.target.value)}
        options={categorieen.map((c) => ({ value: c.id, label: c.label }))}
      />
      <TextareaField
        label={t("logboek.wat")}
        value={tekst}
        onChange={(e) => setTekst(e.target.value)}
        placeholder={t("logboek.watPlaceholder")}
        rows={3}
      />
    </Modal>
  );
}

// ── groeperen en opmaken ────────────────────────────────────────────────────────────────────────

interface DagGroep {
  sleutel: string;
  kop: string;
  regels: RegelResponse[];
}

/**
 * Per dag, met "Vandaag" en "Gisteren" uitgeschreven.
 *
 * <p>De server levert de regels al op volgorde (nieuwste eerst); hier wordt alleen gegroepeerd, niet
 * opnieuw gesorteerd. Zou dit scherm zelf sorteren, dan hangt de volgorde aan de klok van deze
 * browser in plaats van aan die van de server.
 */
function groepeerPerDag(
  regels: RegelResponse[],
  locale: string,
  t: (key: string, options?: unknown) => string,
): DagGroep[] {
  const vandaag = localDateIso(new Date());
  const gisteren = localDateIso(new Date(Date.now() - 864e5));
  const groepen: DagGroep[] = [];

  for (const regel of regels) {
    const datum = new Date(regel.gebeurdOp);
    const sleutel = Number.isNaN(datum.getTime()) ? regel.gebeurdOp : localDateIso(datum);
    let groep = groepen.find((g) => g.sleutel === sleutel);
    if (!groep) {
      groep = { sleutel, kop: dagKop(sleutel, datum, vandaag, gisteren, locale, t), regels: [] };
      groepen.push(groep);
    }
    groep.regels.push(regel);
  }
  return groepen;
}

function dagKop(
  sleutel: string,
  datum: Date,
  vandaag: string,
  gisteren: string,
  locale: string,
  t: (key: string, options?: unknown) => string,
): string {
  if (sleutel === vandaag) return t("logboek.vandaag");
  if (sleutel === gisteren) return t("logboek.gisteren");
  if (Number.isNaN(datum.getTime())) return sleutel;
  return datum.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" });
}

function tijd(iso: string, locale: string): string {
  const datum = new Date(iso);
  if (Number.isNaN(datum.getTime())) return "";
  return datum.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
}

/**
 * De iconen zijn opmaak en staan daarom hier, niet in {@code @fawkes/ui}: ze horen bij deze vier
 * categorieën en nergens anders, en een tegelmigratie hoort de gedeelde componentenbibliotheek niet
 * te verbreden.
 */
function CategorieIcoon({ categorie }: { categorie: string }) {
  const gedeeld = { width: 14, height: 14, viewBox: "0 0 24 24", "aria-hidden": true as const };
  switch (categorie) {
    case "VERZORGING": // pootje
      return (
        <svg {...gedeeld} fill="currentColor">
          <circle cx="7" cy="8" r="2.1" />
          <circle cx="12" cy="6" r="2.1" />
          <circle cx="17" cy="8" r="2.1" />
          <circle cx="19" cy="13" r="1.8" />
          <path d="M12 11c-2.8 0-5 2.2-5 4.6 0 1.7 1.3 2.8 3 2.8h4c1.7 0 3-1.1 3-2.8C17 13.2 14.8 11 12 11Z" />
        </svg>
      );
    case "SCHOONMAAK": // bezem
      return (
        <svg {...gedeeld} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M17 3 9.5 10.5" />
          <path d="M6 13h7l1.5 8H4.5L6 13Z" strokeLinejoin="round" />
          <path d="M9 16v3M12 16v3" />
        </svg>
      );
    case "VOEDING": // voerbak
      return (
        <svg {...gedeeld} fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round">
          <path d="M3 11h18l-2.2 7.4a2 2 0 0 1-1.9 1.4H7.1a2 2 0 0 1-1.9-1.4L3 11Z" />
          <path d="M7 11c0-2.8 2.2-5 5-5s5 2.2 5 5" strokeLinecap="round" />
        </svg>
      );
    case "OBSERVATIE": // oog
      return (
        <svg {...gedeeld} fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M2 12s3.6-6 10-6 10 6 10 6-3.6 6-10 6-10-6-10-6Z" strokeLinejoin="round" />
          <circle cx="12" cy="12" r="2.6" />
        </svg>
      );
    default:
      return null;
  }
}
